import { CheckOutlined, InfoCircleOutlined } from "@ant-design/icons";
import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip as ChartTooltip,
} from "chart.js";
import {
  Button,
  Card,
  Col,
  DatePicker,
  Alert,
  Empty,
  Divider,
  Form,
  InputNumber,
  Row,
  Select,
  Space,
  Skeleton,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import dayjs, { Dayjs } from "dayjs";
import { ReactNode, useEffect, useMemo, useState } from "react";
import { Bar, Line } from "react-chartjs-2";
import { useTranslation } from "react-i18next";
import {
  IOrderOperationsAnalytics,
  IOrderOperationsAnalyticsQuery,
  IOrderOperationsComparisonMetric,
  IOrderOperationsGranularity,
  IOrderOperationsStageKey,
} from "../../../models/statistcs/respons";
import {
  IApiLoadStatus,
  IStatisticCacheMeta,
} from "../../../models/statistcs/cache";
import CancellationAnalyticsOverview from "./cancellation-overview";
import RefreshBtn from "../../general/table-components/actions/refresh-btn";
import styles from "./style.module.scss";

ChartJS.register(
  CategoryScale,
  LinearScale,
  LineElement,
  PointElement,
  BarElement,
  Title,
  ChartTooltip,
  Legend,
);

interface IProps {
  analytics?: IOrderOperationsAnalytics;
  loading: boolean;
  status?: IApiLoadStatus;
  cacheMeta?: IStatisticCacheMeta;
  errorMessage?: string;
  onRefresh: (query?: IOrderOperationsAnalyticsQuery) => Promise<void>;
  children: ReactNode;
}

type DateField =
  | "startDate"
  | "endDate"
  | "compareStartDate"
  | "compareEndDate";
type FieldErrors = Partial<Record<DateField, string>>;

const DATE_FORMAT = "YYYY-MM-DD";
const { RangePicker } = DatePicker;

const nullableNumber = (value: unknown) =>
  typeof value === "number" && Number.isFinite(value) ? value : null;

const countNumber = (value: unknown) =>
  typeof value === "number" && Number.isFinite(value) ? value : 0;

const STAGE_KEYS: IOrderOperationsStageKey[] = [
  "storeAcceptance",
  "preparing",
  "customerAcceptance",
  "accountingFinalization",
  "driverSearch",
  "delivery",
  "orderCycle",
];

const STAGE_COMPARISON_KEYS = STAGE_KEYS.filter((key) => key !== "orderCycle");

const STAGE_LABEL_KEYS: Record<IOrderOperationsStageKey, string> = {
  storeAcceptance: "store_acceptance_time_stats",
  preparing: "preparing_time_stats",
  customerAcceptance: "modified_order_acceptance_time_stats",
  accountingFinalization: "accounting_finalization_time_stats",
  driverSearch: "driver_search_time_stats",
  delivery: "area_delivery_time_stats",
  orderCycle: "order_cycle_time_stats",
};

const STAGE_COLORS: Record<IOrderOperationsStageKey, string> = {
  storeAcceptance: "rgba(24, 144, 255, 0.95)",
  preparing: "rgba(250, 173, 20, 0.95)",
  customerAcceptance: "rgba(114, 46, 209, 0.9)",
  accountingFinalization: "rgba(19, 194, 194, 0.95)",
  driverSearch: "rgba(245, 34, 45, 0.9)",
  delivery: "rgba(82, 196, 26, 0.95)",
  orderCycle: "rgba(47, 84, 235, 0.95)",
};

const GRANULARITY_OPTIONS: IOrderOperationsGranularity[] = [
  "day",
  "week",
  "month",
];

const getMetricValue = (item: Record<string, any> | undefined) =>
  nullableNumber(
    item?.p90Minutes ??
      item?.p90CycleMinutes ??
      item?.medianMinutes ??
      item?.averageMinutes,
  );

const metricSource = (item: Record<string, any> | undefined) => {
  if (nullableNumber(item?.p90Minutes ?? item?.p90CycleMinutes) !== null) {
    return "p90";
  }
  if (nullableNumber(item?.medianMinutes) !== null) return "median";
  if (nullableNumber(item?.averageMinutes) !== null) return "average";
  return "no_data";
};

const extractFieldError = (error: any, field: DateField) => {
  const errors = error?.response?.data?.errors;
  const value = errors?.[field];

  if (Array.isArray(value)) return value.filter(Boolean).join(", ");
  if (typeof value === "string") return value;

  return undefined;
};

const getByPath = (source: Record<string, any> | undefined, path: string) =>
  path.split(".").reduce<any>((current, key) => current?.[key], source);

const comparisonMetric = (
  source: Record<string, any> | undefined,
  ...paths: string[]
): IOrderOperationsComparisonMetric | undefined => {
  for (const path of paths) {
    const value = getByPath(source, path);
    if (value && typeof value === "object") return value;
  }

  return undefined;
};

const compactLabel = (label: string) => {
  const text = label || "-";

  if (text.includes("/")) {
    return text
      .split("/")
      .map((part) => part.trim())
      .filter(Boolean)
      .slice(0, 3)
      .map((part) => (part.length > 24 ? `${part.slice(0, 24)}...` : part));
  }

  if (text.length <= 30) return text;

  const words = text.split(/\s+/).filter(Boolean);
  if (words.length <= 1) return `${text.slice(0, 30)}...`;

  const lines: string[] = [];
  let current = "";

  words.forEach((word) => {
    const next = current ? `${current} ${word}` : word;
    if (next.length > 30 && current) {
      lines.push(current);
      current = word;
    } else {
      current = next;
    }
  });

  if (current) lines.push(current);
  return lines.length > 2 ? [lines[0], `${lines[1]}...`] : lines;
};

const OrderOperationsAnalytics = ({
  analytics,
  loading,
  status = "idle",
  cacheMeta,
  errorMessage,
  onRefresh,
  children,
}: IProps) => {
  const { t, i18n } = useTranslation();
  const [startDate, setStartDate] = useState<string | undefined>(
    analytics?.startDate,
  );
  const [endDate, setEndDate] = useState<string | undefined>(
    analytics?.endDate,
  );
  const [compareStartDate, setCompareStartDate] = useState<string | undefined>(
    undefined,
  );
  const [compareEndDate, setCompareEndDate] = useState<string | undefined>(
    undefined,
  );
  const [granularity, setGranularity] =
    useState<IOrderOperationsGranularity>("day");
  const [topAreasLimit, setTopAreasLimit] = useState<number | null | undefined>(
    analytics?.topAreasLimit,
  );
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const isPending = status === "pending" || analytics?.pending === true;
  const isInitialLoading = status === "loading" && !analytics;
  const hasReadyAnalytics = Boolean(analytics && analytics.pending !== true);

  const numberFormatter = useMemo(
    () => new Intl.NumberFormat(i18n.language, { maximumFractionDigits: 1 }),
    [i18n.language],
  );

  useEffect(() => {
    setStartDate(analytics?.startDate);
    setEndDate(analytics?.endDate);
    setCompareStartDate(analytics?.compareStartDate);
    setCompareEndDate(analytics?.compareEndDate);
    setGranularity(analytics?.granularity || "day");
    setTopAreasLimit(analytics?.topAreasLimit);
  }, [
    analytics?.compareEndDate,
    analytics?.compareStartDate,
    analytics?.endDate,
    analytics?.granularity,
    analytics?.startDate,
    analytics?.topAreasLimit,
  ]);

  const disabledFutureDate = (date: Dayjs) =>
    date && date.isAfter(dayjs(), "day");

  const formatMinutes = (value: unknown) => {
    const number = nullableNumber(value);
    return number === null
      ? "-"
      : `${numberFormatter.format(number)} ${t("minutes")}`;
  };

  const formatNumber = (value: unknown) => {
    const number = nullableNumber(value);
    return number === null ? "-" : numberFormatter.format(number);
  };

  const formatPercent = (value: unknown) => {
    const number = nullableNumber(value);
    return number === null ? "-" : `${numberFormatter.format(number)}%`;
  };

  const formatCacheDate = (value?: string | null) =>
    value ? dayjs(value).format("YYYY-MM-DD HH:mm") : undefined;

  const formatDuration = (seconds?: number | null) => {
    if (!seconds) return undefined;

    if (seconds >= 3600 && seconds % 3600 === 0) {
      return `${seconds / 3600} ${t("hours")}`;
    }

    if (seconds >= 60 && seconds % 60 === 0) {
      return `${seconds / 60} ${t("minutes")}`;
    }

    return `${seconds} ${t("seconds")}`;
  };

  const normalizeBackendLabel = (label: string | undefined) => {
    if (!label) return "";

    return label
      .replace(
        /تقرير زمن قبول الطلب المعدل/g,
        t("modified_order_acceptance_time_report"),
      )
      .replace(
        /زمن قبول الطلب المعدل/g,
        t("modified_order_acceptance_time_stats"),
      );
  };

  const stageLabel = (
    key: string | undefined,
    fallback: string | undefined = "",
  ) =>
    key && STAGE_LABEL_KEYS[key as IOrderOperationsStageKey]
      ? t(STAGE_LABEL_KEYS[key as IOrderOperationsStageKey])
      : normalizeBackendLabel(fallback) || "-";

  const formatPeriodLabel = (item: {
    date?: string;
    startDate?: string;
    label?: string;
  }) => {
    const source = item.startDate || item.date || item.label || "-";
    const parsed = dayjs(source, DATE_FORMAT, true);

    if (!parsed.isValid()) {
      const label = item.label || source;
      return label.length > 12 ? `${label.slice(0, 12)}...` : label;
    }

    return granularity === "month"
      ? parsed.format("YYYY-MM")
      : parsed.format("MM-DD");
  };

  const renderTrendTag = (
    metric: IOrderOperationsComparisonMetric | undefined,
    lowerIsBetter = true,
  ) => {
    const delta = nullableNumber(metric?.delta);
    if (delta === null || delta === 0) return null;

    const isBetter = lowerIsBetter ? delta < 0 : delta > 0;
    const sign = delta > 0 ? "+" : "";
    const rate = nullableNumber(metric?.deltaRate);

    return (
      <Tooltip
        title={`${t("comparison_change_tooltip")} ${t(
          "current_period",
        )}: ${formatNumber(metric?.current)} | ${t(
          "previous_period",
        )}: ${formatNumber(metric?.previous)}`}
      >
        <Tag className={styles.trendTag} color={isBetter ? "success" : "error"}>
          {sign}
          {formatNumber(delta)}
          {rate !== null ? ` (${sign}${formatPercent(rate)})` : ""}
        </Tag>
      </Tooltip>
    );
  };

  const validateDates = () => {
    const errors: FieldErrors = {};
    const today = dayjs();
    const start = startDate ? dayjs(startDate, DATE_FORMAT, true) : null;
    const end = endDate ? dayjs(endDate, DATE_FORMAT, true) : null;
    const compareStart = compareStartDate
      ? dayjs(compareStartDate, DATE_FORMAT, true)
      : null;
    const compareEnd = compareEndDate
      ? dayjs(compareEndDate, DATE_FORMAT, true)
      : null;

    if (startDate && (!start?.isValid() || start.isAfter(today, "day"))) {
      errors.startDate = t("invalid_date");
    }

    if (endDate && (!end?.isValid() || end.isAfter(today, "day"))) {
      errors.endDate = t("invalid_date");
    }

    if (start?.isValid() && end?.isValid() && end.isBefore(start, "day")) {
      errors.endDate = t("end_date_before_start_date");
    }

    if (
      compareStartDate &&
      (!compareStart?.isValid() || compareStart.isAfter(today, "day"))
    ) {
      errors.compareStartDate = t("invalid_date");
    }

    if (
      compareEndDate &&
      (!compareEnd?.isValid() || compareEnd.isAfter(today, "day"))
    ) {
      errors.compareEndDate = t("invalid_date");
    }

    if (compareStartDate && !compareEndDate) {
      errors.compareEndDate = t("comparison_dates_required");
    }

    if (!compareStartDate && compareEndDate) {
      errors.compareStartDate = t("comparison_dates_required");
    }

    if (
      compareStart?.isValid() &&
      compareEnd?.isValid() &&
      compareEnd.isBefore(compareStart, "day")
    ) {
      errors.compareEndDate = t("end_date_before_start_date");
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const refresh = async () => {
    if (!validateDates()) return;

    try {
      const hasComparisonDates = Boolean(compareStartDate && compareEndDate);

      setFieldErrors({});
      await onRefresh({
        startDate,
        endDate,
        granularity,
        topAreasLimit: topAreasLimit ?? undefined,
        compareStartDate: hasComparisonDates ? compareStartDate : undefined,
        compareEndDate: hasComparisonDates ? compareEndDate : undefined,
      });
    } catch (error) {
      setFieldErrors({
        startDate: extractFieldError(error, "startDate"),
        endDate: extractFieldError(error, "endDate"),
        compareStartDate: extractFieldError(error, "compareStartDate"),
        compareEndDate: extractFieldError(error, "compareEndDate"),
      });
    }
  };

  const summary = analytics?.lifecycle?.summary || {};
  const stages = analytics?.lifecycle?.stages;
  const orderCycle = stages?.orderCycle;
  const lifecycleComparison = analytics?.lifecycle?.previousPeriodComparison;
  const p90OrderCycleComparison = comparisonMetric(
    lifecycleComparison,
    "summary.p90OrderCycleMinutes",
    "stages.orderCycle.p90Minutes",
    "orderCycle.p90Minutes",
    "p90OrderCycleMinutes",
  );
  const stageCoverageComparison = comparisonMetric(
    lifecycleComparison,
    "summary.stageCoverageRate",
    "stages.orderCycle.stageCoverageRate",
    "orderCycle.stageCoverageRate",
    "stageCoverageRate",
  );
  const p90OrderCycle =
    summary.p90OrderCycleMinutes ?? orderCycle?.p90Minutes ?? null;
  const stageCoverage =
    summary.stageCoverageRate ?? orderCycle?.extraMetrics?.stageCoverageRate;
  const totalExcluded =
    summary.totalExcludedOrderCount ??
    Object.values(stages || {}).reduce(
      (total, stage) => total + (stage?.excludedOrderCount || 0),
      0,
    );
  const stageItems = STAGE_KEYS.map((key) => {
    const stage = stages?.[key];
    const value = getMetricValue(stage);
    return {
      key,
      label: stageLabel(key, stage?.label),
      stage,
      value,
      metricSource: metricSource(stage),
    };
  });
  const comparableStageItems = stageItems.filter(
    (stage) => stage.key !== "orderCycle",
  );
  const slowestStage =
    comparableStageItems
      .filter((stage) => stage.value !== null)
      .sort((a, b) => (b.value || 0) - (a.value || 0))[0] || undefined;
  const maxStageValue = Math.max(
    0,
    ...comparableStageItems.map((stage) => stage.value || 0),
  );
  const chartTextColor =
    document.body.classList.contains("dark") ||
    document.documentElement.getAttribute("data-theme") === "dark"
      ? "rgba(255,255,255,0.78)"
      : "rgba(0,0,0,0.72)";
  const chartGridColor =
    document.body.classList.contains("dark") ||
    document.documentElement.getAttribute("data-theme") === "dark"
      ? "rgba(255,255,255,0.08)"
      : "rgba(0,0,0,0.08)";
  const stageChartData = {
    labels: comparableStageItems.map((item) => compactLabel(item.label)),
    datasets: [
      {
        label: t("stage_duration_minutes"),
        data: comparableStageItems.map((item) => item.value),
        backgroundColor: comparableStageItems.map((item) =>
          item.key === slowestStage?.key
            ? "rgba(250, 173, 20, 0.55)"
            : "rgba(24, 144, 255, 0.38)",
        ),
        borderColor: comparableStageItems.map((item) =>
          item.key === slowestStage?.key
            ? "rgba(250, 173, 20, 0.95)"
            : "rgba(24, 144, 255, 0.95)",
        ),
        borderWidth: 1,
        borderRadius: 4,
      },
    ],
  };
  const barOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (context: any) => {
            const value = nullableNumber(
              context.parsed?.y ?? context.parsed?.x,
            );
            return `${context.dataset.label}: ${formatMinutes(value)}`;
          },
        },
      },
    },
    scales: {
      x: {
        ticks: {
          color: chartTextColor,
          maxRotation: 0,
          minRotation: 0,
          font: { size: 11 },
        },
        grid: { display: false },
      },
      y: {
        beginAtZero: true,
        ticks: { color: chartTextColor, font: { size: 11 } },
        grid: { color: chartGridColor },
      },
    },
  };
  const horizontalBarOptions = {
    ...barOptions,
    indexAxis: "y" as const,
    scales: {
      x: {
        beginAtZero: true,
        ticks: { color: chartTextColor, font: { size: 11 } },
        grid: { color: chartGridColor },
      },
      y: {
        ticks: { color: chartTextColor, autoSkip: false, font: { size: 10 } },
        grid: { display: false },
      },
    },
  };
  const lineOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: "index" as const,
      intersect: false,
    },
    plugins: {
      legend: {
        position: "bottom" as const,
        labels: {
          color: chartTextColor,
          boxWidth: 10,
          padding: 12,
          font: { size: 11 },
        },
      },
      tooltip: {
        callbacks: {
          title: (items: any[]) => {
            const item = items?.[0]?.raw?.period;
            return item?.startDate && item?.endDate
              ? `${item.startDate} - ${item.endDate}`
              : items?.[0]?.label || "";
          },
          label: (context: any) =>
            `${context.dataset.label}: ${formatMinutes(context.raw?.value)}`,
        },
      },
    },
    scales: {
      x: {
        ticks: {
          color: chartTextColor,
          maxRotation: 0,
          minRotation: 0,
          maxTicksLimit: 9,
          font: { size: 11 },
        },
        grid: { display: false },
      },
      y: {
        beginAtZero: true,
        ticks: { color: chartTextColor, font: { size: 11 } },
        grid: { color: chartGridColor },
      },
    },
  };
  const bucketDistribution = analytics?.lifecycle?.bucketDistribution || [];
  const bucketChartData = {
    labels: bucketDistribution.map((bucket: any) =>
      compactLabel(bucket.bucketLabel),
    ),
    datasets: [
      {
        label: t("orders"),
        data: bucketDistribution.map((bucket: any) =>
          countNumber(bucket.ordersCount ?? bucket.orderCount),
        ),
        backgroundColor: "rgba(19, 194, 194, 0.38)",
        borderColor: "rgba(19, 194, 194, 0.95)",
        borderWidth: 1,
        borderRadius: 4,
      },
    ],
  };
  const phaseBreakdown = analytics?.lifecycle?.phaseBreakdown || [];
  const phaseChartData = {
    labels: phaseBreakdown.map((phase: any) => compactLabel(phase.phaseName)),
    datasets: [
      {
        label: t("average"),
        data: phaseBreakdown.map((phase: any) =>
          nullableNumber(phase.averageMinutes),
        ),
        backgroundColor: phaseBreakdown.map((phase: any) =>
          phase.phaseKey === "unattributed"
            ? "rgba(140, 140, 140, 0.42)"
            : "rgba(114, 46, 209, 0.34)",
        ),
        borderColor: phaseBreakdown.map((phase: any) =>
          phase.phaseKey === "unattributed"
            ? "rgba(140, 140, 140, 0.95)"
            : "rgba(114, 46, 209, 0.9)",
        ),
        borderWidth: 1,
        borderRadius: 4,
      },
    ],
  };
  const areaStageMatrix = analytics?.lifecycle?.areaStageMatrix || [];
  const heatmapRows = areaStageMatrix.slice(0, 10);
  const heatmapMax = Math.max(
    0,
    ...heatmapRows.flatMap((row: any) =>
      STAGE_COMPARISON_KEYS.map(
        (key) => getMetricValue(row?.stages?.[key]) || 0,
      ),
    ),
  );
  const lifecycleTimeSeries = analytics?.lifecycle?.timeSeries || [];
  const lifecycleTrendData = {
    labels: lifecycleTimeSeries.map(formatPeriodLabel),
    datasets: STAGE_COMPARISON_KEYS.map((key) => ({
      label: t(STAGE_LABEL_KEYS[key]),
      data: lifecycleTimeSeries.map((item) => {
        const value = nullableNumber(item.stages?.[key]?.p90Minutes);
        return {
          x: formatPeriodLabel(item),
          y: value,
          value,
          period: item,
        };
      }),
      borderColor: STAGE_COLORS[key],
      backgroundColor: STAGE_COLORS[key].replace("0.95", "0.18"),
      borderWidth: 2,
      pointRadius: 2,
      tension: 0.28,
      spanGaps: true,
      parsing: {
        xAxisKey: "x",
        yAxisKey: "y",
      },
    })),
  };
  const areaStageTimeSeries = analytics?.lifecycle?.areaStageTimeSeries || [];
  const areaStageBottlenecks = areaStageTimeSeries
    .filter(
      (item) =>
        item.stageKey !== "orderCycle" &&
        nullableNumber(item.p90Minutes) !== null,
    )
    .slice()
    .sort((a, b) => (b.p90Minutes || 0) - (a.p90Minutes || 0))
    .slice(0, 12);
  const areaStageBottleneckData = {
    labels: areaStageBottlenecks.map((item) =>
      compactLabel(
        `${item.areaName || "-"} / ${t(STAGE_LABEL_KEYS[item.stageKey])} / ${
          item.label || item.date
        }`,
      ),
    ),
    datasets: [
      {
        label: t("p90"),
        data: areaStageBottlenecks.map((item) => item.p90Minutes),
        backgroundColor: "rgba(250, 173, 20, 0.38)",
        borderColor: "rgba(250, 173, 20, 0.95)",
        borderWidth: 1,
        borderRadius: 4,
      },
    ],
  };
  const areaStageBottleneckHeight = Math.max(
    380,
    areaStageBottlenecks.length * 40 + 96,
  );
  const selectedTopAreasLimit = topAreasLimit ?? null;
  const appliedTopAreasLimit = analytics?.topAreasLimit ?? null;
  const filtersDirty = Boolean(
    analytics &&
      ((startDate || undefined) !== (analytics.startDate || undefined) ||
        (endDate || undefined) !== (analytics.endDate || undefined) ||
        (compareStartDate || undefined) !==
          (analytics.compareStartDate || undefined) ||
        (compareEndDate || undefined) !==
          (analytics.compareEndDate || undefined) ||
        (granularity || "day") !== (analytics.granularity || "day") ||
        selectedTopAreasLimit !== appliedTopAreasLimit),
  );
  const generatedAt = formatCacheDate(cacheMeta?.generatedAt);
  const queuedAt = formatCacheDate(cacheMeta?.queuedAt);
  const retryAfter = formatDuration(cacheMeta?.retryAfterSeconds);
  const refreshInterval = formatDuration(cacheMeta?.refreshIntervalSeconds);

  return (
    <div className={styles.container}>
      <Divider orientation="center">{t("order_operations_analytics")}</Divider>
      <Card className={styles.panel} bodyStyle={{ padding: 16 }}>
        <div className={styles.overviewPanel}>
          <div className={styles.summaryBlock}>
            <div className={styles.summaryGrid}>
              <div className={styles.summaryMetric}>
                <span>{t("p90_order_cycle_time")}</span>
                <strong>{formatMinutes(p90OrderCycle)}</strong>
                {renderTrendTag(p90OrderCycleComparison)}
              </div>
              <div className={styles.summaryMetric}>
                <span>{t("slowest_stage")}</span>
                <strong>
                  {stageLabel(
                    summary.slowestStageKey,
                    summary.slowestStageLabel,
                  )}
                </strong>
              </div>
              <div className={styles.summaryMetric}>
                <span>{t("worst_area")}</span>
                <strong>{summary.worstAreaName || "-"}</strong>
              </div>
              <div className={styles.summaryMetric}>
                <span>{t("stage_coverage_rate")}</span>
                <strong>
                  {nullableNumber(stageCoverage) === null
                    ? "-"
                    : `${formatNumber(stageCoverage)}%`}
                </strong>
                {renderTrendTag(stageCoverageComparison, false)}
              </div>
            </div>
            <div className={styles.periodMeta}>
              <span>
                {analytics?.startDate && analytics?.endDate
                  ? `${analytics.startDate} - ${analytics.endDate}`
                  : t("order_statistics_default_period")}
              </span>
              {analytics?.compareStartDate && analytics?.compareEndDate && (
                <span>
                  {t("previous_period")}: {analytics.compareStartDate} -{" "}
                  {analytics.compareEndDate}
                </span>
              )}
              <span>
                {t("granularity")}: {t(analytics?.granularity || granularity)}
              </span>
              <span>
                {t("top_areas_limit")}:{" "}
                {analytics?.topAreasLimit ?? t("all_areas")}
              </span>
              <span>
                {t("data_quality_exclusions")}: {formatNumber(totalExcluded)}
              </span>
            </div>
            {analytics?.compareStartDate && analytics?.compareEndDate && (
              <div className={styles.comparisonUsage}>
                <InfoCircleOutlined />
                <span>
                  <strong>{t("comparison_usage_title")}</strong>
                  {t("comparison_usage_hint")}
                </span>
              </div>
            )}
            <div className={styles.readingGuide}>
              <span>
                <strong>{t("p90")}</strong>
                {t("p90_dashboard_hint")}
              </span>
              <span>
                <strong>{t("included_orders_short")}</strong>
                {t("included_orders_dashboard_hint")}
              </span>
              <span>
                <strong>{t("excluded_orders_short")}</strong>
                {t("excluded_orders_dashboard_hint")}
              </span>
            </div>
          </div>
          <div className={styles.filterBlock}>
            <div className={styles.filterGrid}>
              <Form.Item
                label={t("current_period")}
                validateStatus={
                  fieldErrors.startDate || fieldErrors.endDate
                    ? "error"
                    : undefined
                }
                help={fieldErrors.startDate || fieldErrors.endDate}
              >
                <RangePicker
                  allowClear
                  disabledDate={disabledFutureDate}
                  format={DATE_FORMAT}
                  placeholder={[t("startDate"), t("endDate")]}
                  value={[
                    startDate ? dayjs(startDate) : null,
                    endDate ? dayjs(endDate) : null,
                  ]}
                  onChange={(_, values) => {
                    setStartDate(values[0] || undefined);
                    setEndDate(values[1] || undefined);
                  }}
                />
              </Form.Item>
              <Form.Item label={t("granularity")}>
                <Select
                  value={granularity}
                  onChange={setGranularity}
                  options={GRANULARITY_OPTIONS.map((value) => ({
                    label: t(value),
                    value,
                  }))}
                />
              </Form.Item>
              <Form.Item
                label={t("top_areas_limit")}
                extra={t("top_areas_limit_all_hint")}
              >
                <InputNumber
                  min={1}
                  max={100}
                  placeholder={t("all_areas")}
                  value={topAreasLimit ?? null}
                  onChange={(value) =>
                    setTopAreasLimit(typeof value === "number" ? value : null)
                  }
                />
              </Form.Item>
              <Form.Item
                label={t("comparison_period")}
                validateStatus={
                  fieldErrors.compareStartDate || fieldErrors.compareEndDate
                    ? "error"
                    : undefined
                }
                help={
                  fieldErrors.compareStartDate || fieldErrors.compareEndDate
                }
                extra={t("comparison_dates_optional_hint")}
              >
                <RangePicker
                  allowClear
                  disabledDate={disabledFutureDate}
                  format={DATE_FORMAT}
                  placeholder={[t("compare_start_date"), t("compare_end_date")]}
                  value={[
                    compareStartDate ? dayjs(compareStartDate) : null,
                    compareEndDate ? dayjs(compareEndDate) : null,
                  ]}
                  onChange={(_, values) => {
                    setCompareStartDate(values[0] || undefined);
                    setCompareEndDate(values[1] || undefined);
                  }}
                />
              </Form.Item>
            </div>
            <div className={styles.filterActions}>
              {filtersDirty ? (
                <Button
                  type="primary"
                  icon={<CheckOutlined />}
                  loading={loading || isPending}
                  onClick={refresh}
                  className={styles.refreshButton}
                >
                  {t("apply")}
                </Button>
              ) : (
                <RefreshBtn
                  loading={loading || isPending}
                  onClick={refresh}
                  className={styles.refreshButton}
                >
                  {t("refresh")}
                </RefreshBtn>
              )}
            </div>
          </div>
          {(cacheMeta || isPending || errorMessage) && (
            <div className={styles.cacheMetaBar}>
              {isPending && (
                <Tag color="processing">{t("statistics_data_preparing")}</Tag>
              )}
              {generatedAt && (
                <span>
                  {t("statistics_last_generated_at", { value: generatedAt })}
                </span>
              )}
              {queuedAt && isPending && (
                <span>{t("statistics_queued_at", { value: queuedAt })}</span>
              )}
              {retryAfter && isPending && (
                <span>
                  {t("statistics_retry_after", { value: retryAfter })}
                </span>
              )}
              {refreshInterval && (
                <span>
                  {t("statistics_refresh_interval", {
                    value: refreshInterval,
                  })}
                </span>
              )}
            </div>
          )}
        </div>
      </Card>
      {errorMessage && (
        <Alert
          className={styles.stateAlert}
          type="error"
          showIcon
          message={errorMessage}
        />
      )}
      {isPending && (
        <Alert
          className={styles.stateAlert}
          type="info"
          showIcon
          message={
            hasReadyAnalytics
              ? t("statistics_data_updating_message")
              : t("statistics_data_pending_message")
          }
        />
      )}
      {isInitialLoading || (isPending && !hasReadyAnalytics) ? (
        <Card className={styles.panel} bodyStyle={{ padding: 16 }}>
          <Skeleton active paragraph={{ rows: 8 }} />
        </Card>
      ) : hasReadyAnalytics ? (
        <>
          <div className={styles.storyGrid}>
            <Card className={styles.panel} bodyStyle={{ padding: 16 }}>
              <div className={styles.sectionHeader}>
                <div className={styles.headingLine}>
                  <Typography.Text strong>
                    {t("completed_order_journey")}
                  </Typography.Text>
                  <Tooltip title={t("stage_metric_source_hint")}>
                    <InfoCircleOutlined />
                  </Tooltip>
                </div>
                <Typography.Text type="secondary">
                  {t("completed_order_journey_hint")}
                </Typography.Text>
              </div>
              <div className={styles.stageRail}>
                {stageItems.map((item, index) => (
                  <div
                    key={item.key}
                    className={`${styles.stageTile} ${
                      item.key === slowestStage?.key ? styles.warningStage : ""
                    } ${item.key === "orderCycle" ? styles.resultStage : ""}`}
                  >
                    <div className={styles.stageIndex}>
                      <span>{t("phase")}</span>
                      <strong>{index + 1}</strong>
                    </div>
                    <div className={styles.stageContent}>
                      <Typography.Text className={styles.stageLabel} strong>
                        {item.label}
                      </Typography.Text>
                      <div className={styles.stageValue}>
                        {formatMinutes(item.value)}
                      </div>
                      <div className={styles.stageMetaGrid}>
                        <span>
                          <small>{t("metric_source")}</small>
                          <strong>{t(item.metricSource)}</strong>
                        </span>
                        <span>
                          <small>{t("included_orders_short")}</small>
                          <strong>
                            {formatNumber(item.stage?.includedOrderCount)}
                          </strong>
                        </span>
                        <span>
                          <small>{t("excluded_orders_short")}</small>
                          <strong>
                            {formatNumber(
                              countNumber(item.stage?.excludedOrderCount),
                            )}
                          </strong>
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </Card>

            <Row gutter={[16, 16]} className={styles.chartRow}>
              <Col xs={24} xl={14}>
                <Card className={styles.chartCard} bodyStyle={{ padding: 16 }}>
                  <div className={styles.sectionHeader}>
                    <div className={styles.headingLine}>
                      <Typography.Text strong>
                        {t("stage_trend_chart")}
                      </Typography.Text>
                      <Tooltip title={t("stage_trend_chart_tooltip")}>
                        <InfoCircleOutlined />
                      </Tooltip>
                    </div>
                    <Typography.Text type="secondary">
                      {t("stage_trend_chart_hint")}
                    </Typography.Text>
                  </div>
                  <div className={styles.wideChart}>
                    {lifecycleTimeSeries.length > 0 ? (
                      <Line data={lifecycleTrendData} options={lineOptions} />
                    ) : (
                      <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
                    )}
                  </div>
                </Card>
              </Col>
              <Col xs={24} xl={10}>
                <Card className={styles.chartCard} bodyStyle={{ padding: 16 }}>
                  <div className={styles.sectionHeader}>
                    <div className={styles.headingLine}>
                      <Typography.Text strong>
                        {t("area_stage_period_bottlenecks")}
                      </Typography.Text>
                      <Tooltip
                        title={t("area_stage_period_bottlenecks_tooltip")}
                      >
                        <InfoCircleOutlined />
                      </Tooltip>
                    </div>
                    <Typography.Text type="secondary">
                      {t("area_stage_period_bottlenecks_hint")}
                    </Typography.Text>
                  </div>
                  <div
                    className={`${styles.chart} ${styles.tallChart}`}
                    style={{ height: areaStageBottleneckHeight }}
                  >
                    {areaStageBottlenecks.length > 0 ? (
                      <Bar
                        data={areaStageBottleneckData}
                        options={horizontalBarOptions}
                      />
                    ) : (
                      <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
                    )}
                  </div>
                </Card>
              </Col>
            </Row>

            <Row gutter={[16, 16]}>
              <Col xs={24} md={12} xl={12}>
                <Card className={styles.chartCard} bodyStyle={{ padding: 16 }}>
                  <div className={styles.sectionHeader}>
                    <div className={styles.headingLine}>
                      <Typography.Text strong>
                        {t("stage_bottleneck_chart")}
                      </Typography.Text>
                      <Tooltip title={t("stage_bottleneck_chart_tooltip")}>
                        <InfoCircleOutlined />
                      </Tooltip>
                    </div>
                    <Typography.Text type="secondary">
                      {slowestStage
                        ? `${t("slowest_stage")}: ${slowestStage.label}`
                        : t("no_data")}
                    </Typography.Text>
                  </div>
                  <div className={styles.chart}>
                    {maxStageValue > 0 ? (
                      <Bar data={stageChartData} options={barOptions} />
                    ) : (
                      <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
                    )}
                  </div>
                </Card>
              </Col>
              <Col xs={24} md={12} xl={12}>
                <Card className={styles.chartCard} bodyStyle={{ padding: 16 }}>
                  <div className={styles.sectionHeader}>
                    <div className={styles.headingLine}>
                      <Typography.Text strong>
                        {t("bucket_distribution")}
                      </Typography.Text>
                      <Tooltip title={t("bucket_distribution_tooltip")}>
                        <InfoCircleOutlined />
                      </Tooltip>
                    </div>
                    <Typography.Text type="secondary">
                      {t("cycle_duration_bucket")}
                    </Typography.Text>
                  </div>
                  <div className={styles.chart}>
                    {bucketDistribution.length > 0 ? (
                      <Bar data={bucketChartData} options={barOptions} />
                    ) : (
                      <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
                    )}
                  </div>
                </Card>
              </Col>
              <Col xs={24} md={12} xl={12}>
                <Card className={styles.chartCard} bodyStyle={{ padding: 16 }}>
                  <div className={styles.sectionHeader}>
                    <Space className={styles.headingLine}>
                      <Typography.Text strong>
                        {t("phase_breakdown")}
                      </Typography.Text>
                      <Tooltip
                        title={t("phase_breakdown_unattributed_tooltip")}
                      >
                        <InfoCircleOutlined />
                      </Tooltip>
                    </Space>
                    <Typography.Text type="secondary">
                      {t("share_of_known_cycle_minutes")}
                    </Typography.Text>
                  </div>
                  <div className={styles.chart}>
                    {phaseBreakdown.length > 0 ? (
                      <Bar data={phaseChartData} options={barOptions} />
                    ) : (
                      <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
                    )}
                  </div>
                </Card>
              </Col>
              <Col xs={24} md={12} xl={12}>
                <Card className={styles.chartCard} bodyStyle={{ padding: 16 }}>
                  <div className={styles.sectionHeader}>
                    <div className={styles.headingLine}>
                      <Typography.Text strong>
                        {t("area_stage_heatmap")}
                      </Typography.Text>
                      <Tooltip title={t("area_stage_heatmap_tooltip")}>
                        <InfoCircleOutlined />
                      </Tooltip>
                    </div>
                    <Typography.Text type="secondary">
                      {t("area_stage_heatmap_hint")}
                    </Typography.Text>
                  </div>
                  {heatmapRows.length > 0 ? (
                    <div className={styles.heatmap}>
                      <div className={styles.heatmapHeader}>
                        <span>{t("area")}</span>
                        {STAGE_COMPARISON_KEYS.map((key) => (
                          <span key={key}>{t(STAGE_LABEL_KEYS[key])}</span>
                        ))}
                      </div>
                      {heatmapRows.map((row: any, index) => (
                        <div
                          className={styles.heatmapRow}
                          key={row.areaId ?? index}
                        >
                          <span className={styles.areaCell}>
                            {row.areaName || "-"}
                          </span>
                          {STAGE_COMPARISON_KEYS.map((key) => {
                            const value = getMetricValue(row?.stages?.[key]);
                            const intensity =
                              value && heatmapMax > 0
                                ? Math.max(0.08, value / heatmapMax)
                                : 0;

                            return (
                              <span
                                key={key}
                                className={styles.heatmapCell}
                                style={{
                                  backgroundColor:
                                    intensity > 0
                                      ? `rgba(24, 144, 255, ${0.08 + intensity * 0.42})`
                                      : undefined,
                                }}
                              >
                                {formatMinutes(value)}
                              </span>
                            );
                          })}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
                  )}
                </Card>
              </Col>
            </Row>
          </div>
          <CancellationAnalyticsOverview
            cancellations={analytics?.cancellations}
            customerCancellation={
              analytics?.cancellations?.customerCancellation
            }
            administrativeCancellation={
              analytics?.cancellations?.administrativeCancellation
            }
          />
          <Divider orientation="center">{t("detailed_reports")}</Divider>
          {children}
        </>
      ) : null}
    </div>
  );
};

export default OrderOperationsAnalytics;
